import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Grid,
    InputLabel,
    OutlinedInput,
    Typography,
    Switch,
    IconButton,
    Tooltip,
    Radio,
    FormControl,
    FormControlLabel,
    RadioGroup
} from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';
import ApiHelper from 'utils/apiHelper';
import { validateInputValue, validateNumber } from 'utils/commonHelpers';
import EditScriptDialog from './EditScriptDialog';
import AddScriptDialog from './AddScriptDialog';

const AdvancedSettings = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const [advancedSettingsData, setAdvancedSettingsData] = useState('');
    const [scriptsData, setScriptsData] = useState([]);
    const [deleteScriptId, setDeleteScriptId] = useState();
    const [selectedScriptData, setSelectedScriptData] = useState();
    const [showEditScriptDialog, setShowEditScriptDialog] = useState(false);
    const [showAddScriptDialog, setShowAddScriptDialog] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    useEffect(() => {
        getAdvancedSettingsData();
        getScriptsData();
    }, []);

    const getAdvancedSettingsData = () => {
        ApiHelper.get('/api/settings/advanced_settings/')
            .then((res) => {
                setAdvancedSettingsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error(err.response.data.message);
                }
            });
    };

    const getScriptsData = () => {
        ApiHelper.get('/api/server/scripts/')
            .then((res) => {
                setScriptsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error(err.response.data.message);
                }
            });
    };

    const handleChangeSwitchData = (e) => {
        setAdvancedSettingsData({
            ...advancedSettingsData,
            [e.target.name]: e.target.checked
        });
    };

    const handleChangeSettingsData = (e) => {
        if (e.target.value === '') {
            toast.error('Please type the value');
        }

        // Allow decimal values for averageBaseline field
        if (e.target.name === 'averageBaseline') {
            if (validateNumber(e.target.value, 'decimal') === true) {
                setAdvancedSettingsData({
                    ...advancedSettingsData,
                    [e.target.name]: e.target.value
                });
            } else {
                toast.error('Please enter a valid decimal number with up to 2 decimal places.');
            }
        } else {
            if (validateNumber(e.target.value, 'integer') === true) {
                setAdvancedSettingsData({
                    ...advancedSettingsData,
                    [e.target.name]: e.target.value
                });
            } else {
                toast.error('It should be an integer value.');
            }
        }
    };

    const isShowCard = (isCard = null) => {
        if (userInfo.admin_permissions.server_admin || userInfo.admin_permissions.root_admin) {
            return true;
        } else return !!isCard;
    };

    const handleSubmit = () => {
        if (
            advancedSettingsData.idleLength === '' ||
            advancedSettingsData.aiLength === '' ||
            advancedSettingsData.chargeMin === '' ||
            advancedSettingsData.preOff === '' ||
            advancedSettingsData.dimmingAmount === '' ||
            advancedSettingsData.inactiveTimeout === ''
        ) {
            toast.error('There are some empty values');
            return true;
        }
        if (
            validateInputValue(advancedSettingsData.dimmingAmount, 'percentage') === false ||
            validateInputValue(advancedSettingsData.chargeMin, 'percentage') === false
        ) {
            toast.error('Percentage value should be between 0 and 100');
            return true;
        }
        ApiHelper.put(`/api/settings/advanced_settings/${advancedSettingsData.id}/`, {
            ai_wake: advancedSettingsData.aiWake,
            ai_audit: advancedSettingsData.aiAudit,
            priority_wake: advancedSettingsData.priorityWake,
            ai_data: advancedSettingsData.aiData,
            idle_length: advancedSettingsData.idleLength,
            ai_length: advancedSettingsData.aiLength,
            charge_min: advancedSettingsData.chargeMin,
            use_off: advancedSettingsData.useOff,
            pre_off: advancedSettingsData.preOff,
            when_charge: advancedSettingsData.whenCharge,
            when_idle: advancedSettingsData.whenIdle,
            demand_reduction: advancedSettingsData.demandReduction,
            dimming: advancedSettingsData.dimmingAmount,
            inactive: advancedSettingsData.inactiveTimeout,
            enable_connections: advancedSettingsData.enableConnections,
            enable_orgapi: advancedSettingsData.enableAPIs,
            enable_autobase: advancedSettingsData.enableAutoBaseline,
            enable_avgbase: advancedSettingsData.useAverageBaseline,
            average_baseline: advancedSettingsData.averageBaseline,
            baseline_length: advancedSettingsData.baselineCaptureLength,
            enable_scripts: advancedSettingsData.enableScripts,
            override: advancedSettingsData.override,
            update_poll: advancedSettingsData.update_poll
        })
            .then(() => {
                toast.success('Successfully updated.');
            })
            .catch(() => {
                toast.error('Failed to update the security settings data');
            });
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeleteScriptId(id);
    };

    const handleClose = () => {
        setShowDeleteConfirmationDialog(false);
    };

    const handleCloseAddScript = () => {
        setShowAddScriptDialog(false);
        getScriptsData();
    };

    const handleCloseEditScript = () => {
        setShowEditScriptDialog(false);
        getScriptsData();
    };

    const handleEditScript = (data) => {
        setSelectedScriptData(data);
        setShowEditScriptDialog(true);
    };

    const handleDelete = () => {
        ApiHelper.delete(`/api/server/scripts/${deleteScriptId}/`)
            .then(() => {
                toast.success('Successfully deleted the script');
                getScriptsData();
                handleClose();
            })
            .catch((err) => {
                toast.error(`Fail! ${err.response.data.message}`);
                handleClose();
            });
    };

    if (!advancedSettingsData) {
        return null;
    } else {
        return (
            <MainCard title="Advanced Settings" cardHeaderTitle="System Options" cardHeaderShow={true}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} sx={{ position: 'relative', mt: 2, mb: 2 }}>
                        <Box sx={{ position: 'absolute', right: '0', top: '0' }}>
                            <Button size="large" color="secondary" variant="contained" onClick={handleSubmit}>
                                Save
                            </Button>
                            <Button onClick={getAdvancedSettingsData}>Cancel</Button>
                        </Box>
                    </Grid>
                    {isShowCard(advancedSettingsData.aiWake) && (
                        <Grid item xs={3}>
                            <SubCard title="Intelligent Update(iUpdate)">
                                <Grid container>
                                    <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center', mb: 2 }}>
                                        <Tooltip title="Enable iUpdate" placement="left">
                                            <InputLabel style={{ marginTop: '10px' }}>Enable iUpdate</InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.aiWake}
                                            name="aiWake"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center', mb: 2 }}>
                                        <Tooltip title="Enable SCCM Active Update" placement="left">
                                            <InputLabel style={{ marginTop: '10px', marginLeft: '20px' }}>
                                                Enable SCCM Active Update
                                            </InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.update_poll}
                                            disabled={!advancedSettingsData.aiWake}
                                            name="update_poll"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                </Grid>
                            </SubCard>
                        </Grid>
                    )}
                    <Grid item xs={3}>
                        <SubCard title="Policy AI">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <Tooltip title="Enable Policy AI" placement="left">
                                        <InputLabel>Enable Policy AI</InputLabel>
                                    </Tooltip>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.enablepolicyai}
                                        name="enablepolicyai"
                                        onChange={handleChangeSwitchData}
                                    />
                                    <Tooltip title="Use Aggressive AI" placement="left">
                                        <InputLabel>Use Aggressive AI</InputLabel>
                                    </Tooltip>
                                    <FormControl>
                                        <RadioGroup
                                            color="success"
                                            value={advancedSettingsData.aggressiveai}
                                            name="aggressiveai"
                                            onChange={handleChangeSwitchData}
                                        >
                                            <FormControlLabel
                                                value={advancedSettingsData.aggressiveai}
                                                control={<Radio />}
                                                label="Aggressive AI"
                                            />
                                            <FormControlLabel
                                                value={!advancedSettingsData.aggressiveai}
                                                control={<Radio />}
                                                label="Non-Aggressive AI"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </SubCard>
                        {userInfo.admin_permissions.root_admin && (
                            <SubCard sx={{ padding: '20px, 0', mb: 2 }} title="Baseline Settings">
                                <Grid container>
                                    <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                        <Tooltip title="Enable Auto-Baseline" placement="left">
                                            <InputLabel>Enable Auto-Baseline</InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.enableAutoBaseline}
                                            name="enableAutoBaseline"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2, mb: 2 }}>
                                        <Tooltip title="Use Average Baseline" placement="left">
                                            <InputLabel>Use Average Baseline</InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.useAverageBaseline}
                                            name="useAverageBaseline"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center' }}>
                                        <Tooltip title="Baseline Capture Length(days)" placement="left">
                                            <Typography
                                                style={{
                                                    marginLeft: '10px',
                                                    marginTop: '5px',
                                                    width: 'calc(100% - 55px)',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                Baseline Capture Length(days)
                                            </Typography>
                                        </Tooltip>
                                        <OutlinedInput
                                            // sx={{
                                            //     width: '50px',
                                            //     height: '30px',
                                            //     marginLeft: '50px',
                                            //     marginTop: '-5px',
                                            //     textAlign: 'left'
                                            // }}
                                            style={{ width: '45px', pl: '10px', height: '30px' }}
                                            value={advancedSettingsData.baselineCaptureLength}
                                            name="baselineCaptureLength"
                                            onChange={handleChangeSettingsData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                        <Tooltip title="Average Baseline" placement="left">
                                            <Typography>Average Baseline</Typography>
                                        </Tooltip>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            value={advancedSettingsData.averageBaseline}
                                            name="averageBaseline"
                                            onChange={handleChangeSettingsData}
                                        />
                                    </Grid>
                                </Grid>
                            </SubCard>
                        )}
                    </Grid>
                    <Grid item xs={3}>
                        {isShowCard(advancedSettingsData.appConnect) && (
                            <SubCard sx={{ padding: '20px, 0', mb: 2 }} title="Raptor Appliance Connections">
                                <Grid container>
                                    <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                        <Tooltip title="Enable Appliance Connections" placement="left">
                                            <InputLabel>Enable Appliance Connections</InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.enableConnections}
                                            disabled={!advancedSettingsData.appConnect}
                                            name="enableConnections"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                </Grid>
                            </SubCard>
                        )}
                        {isShowCard(advancedSettingsData.orgAPI) && (
                            <SubCard sx={{ padding: '20px, 0', mb: 2 }} title="Raptor API Connections">
                                <Grid container>
                                    <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                        <Tooltip title="Enable API Connections" placement="left">
                                            <InputLabel>Enable API Connections</InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.enableAPIs}
                                            name="enableAPIs"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                </Grid>
                            </SubCard>
                        )}
                        {isShowCard(!advancedSettingsData.override) && (
                            <SubCard title="Script Management">
                                <Grid container>
                                    {userInfo.admin_permissions.root_admin && (
                                        <Grid item xs={7} sx={{ display: 'flex', textAlign: 'center', mt: 2, mr: 2 }}>
                                            <Tooltip title="Do NOT allow" placement="left">
                                                <InputLabel style={{ marginTop: '10px' }}>Don't show Scripts</InputLabel>
                                            </Tooltip>
                                            <Switch
                                                color="success"
                                                checked={advancedSettingsData.override}
                                                name="override"
                                                onChange={handleChangeSwitchData}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={6} sx={{ display: 'flex', textAlign: 'center', mt: 2 }}>
                                        <Tooltip title="Enable Scripts" placement="left">
                                            <InputLabel style={{ marginTop: '10px' }}>Enable Scripts</InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.enableScripts}
                                            name="enableScripts"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sx={{ mt: 2 }}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            sx={{ float: 'right', borderRadius: '6px' }}
                                            onClick={() => setShowAddScriptDialog(true)}
                                            startIcon={<Add />}
                                        >
                                            New Script
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                        <Typography>Scripts</Typography>
                                        {scriptsData.map((data, key) => (
                                            <Box key={key} sx={{ display: 'flex' }}>
                                                <IconButton
                                                    size="small"
                                                    //disabled={!advancedSettingsData.enableScripts}
                                                    onClick={() => handleEditScript(data)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    //disabled={!advancedSettingsData.enableScripts}
                                                    onClick={() => handleDeleteConfirmation(data.id)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                                <Typography sx={{ marginTop: '6px' }}>{`- ${data.name}`}</Typography>
                                            </Box>
                                        ))}
                                        {showEditScriptDialog && (
                                            <EditScriptDialog
                                                onCloseCancel={() => setShowEditScriptDialog(false)}
                                                onClose={handleCloseEditScript}
                                                data={selectedScriptData}
                                            />
                                        )}
                                        {showDeleteConfirmationDialog && (
                                            <DeleteConfirmationDialog onClose={handleClose} onYes={handleDelete} name="script" />
                                        )}
                                    </Grid>
                                    {showAddScriptDialog && <AddScriptDialog onClose={handleCloseAddScript} />}
                                </Grid>
                            </SubCard>
                        )}
                    </Grid>
                    <Grid item xs={3}>
                        {isShowCard(advancedSettingsData.demand) && (
                            <SubCard title="Demand Reduction">
                                <Grid container>
                                    <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                        <Tooltip title="Enable Demand Reduction" placement="left">
                                            <InputLabel>Enable Demand Reduction</InputLabel>
                                        </Tooltip>
                                        <Switch
                                            color="success"
                                            // id="passwordSettings"
                                            checked={advancedSettingsData.demandReduction}
                                            disabled={!advancedSettingsData.demand}
                                            name="demandReduction"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                        <Tooltip title="Display Dimming Amount (%)" placement="left">
                                            <Typography>Display Dimming Amount (%)</Typography>
                                        </Tooltip>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            value={advancedSettingsData.dimmingAmount}
                                            disabled={!advancedSettingsData.demandReduction}
                                            name="dimmingAmount"
                                            onChange={handleChangeSettingsData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                        <Tooltip title="Minimum Inactivity Timeout (mins)" placement="left">
                                            <Typography>Minimum Inactivity Timeout (mins)</Typography>
                                        </Tooltip>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            value={advancedSettingsData.inactiveTimeout}
                                            disabled={!advancedSettingsData.demandReduction}
                                            name="inactiveTimeout"
                                            onChange={handleChangeSettingsData}
                                        />
                                    </Grid>
                                </Grid>
                            </SubCard>
                        )}
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default AdvancedSettings;
